import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Download, Film, Image as ImageIcon, Copy } from "lucide-react";
import { toast } from "sonner";

const ASSETS = [
  { file: "tiktok-testimonial-ad.png",    label: "★ Testimonial — TikTok Vertical",     size: "117 KB", ratio: "1080×1920",    Icon: ImageIcon, preview: true },
  { file: "instagram-testimonial-ad.png", label: "★ Testimonial — Instagram Square",    size: "92 KB",  ratio: "1080×1080",    Icon: ImageIcon, preview: true },
  { file: "facebook-testimonial-ad.png",  label: "★ Testimonial — Facebook Landscape",  size: "62 KB",  ratio: "1200×630",     Icon: ImageIcon, preview: true },
  { file: "tiktok-gym-ad.png",     label: "★ Gym Ad + QR — TikTok Vertical",   size: "125 KB", ratio: "1080×1920",    Icon: ImageIcon, preview: true },
  { file: "instagram-gym-ad.png",  label: "★ Gym Ad + QR — Instagram Square",  size: "124 KB", ratio: "1080×1080",    Icon: ImageIcon, preview: true },
  { file: "facebook-gym-ad.png",   label: "★ Gym Ad + QR — Facebook Landscape", size: "80 KB",  ratio: "1200×630",     Icon: ImageIcon, preview: true },
  { file: "tiktok-ad.png",         label: "TikTok Ad — Vertical Story",      size: "239 KB", ratio: "1080×1920",    Icon: ImageIcon, preview: true },
  { file: "instagram-ad.png",      label: "Instagram Ad — Square Feed",      size: "190 KB", ratio: "1080×1080",    Icon: ImageIcon, preview: true },
  { file: "facebook-ad.png",       label: "Facebook Ad — Feed Landscape",    size: "124 KB", ratio: "1200×630",     Icon: ImageIcon, preview: true },
  { file: "refund-ad.mp4",         label: "Video Ad — TikTok/Reels/Stories", size: "162 KB", ratio: "9:16 · 5s", Icon: Film,      preview: true },
  { file: "refund-ad.gif",         label: "Animated GIF — X/Reddit/Discord", size: "528 KB", ratio: "9:16 · 5s", Icon: Film,      preview: true },
  { file: "streaming-story.png",   label: "Streaming Story — TikTok / IG",   size: "220 KB", ratio: "1080×1920",    Icon: ImageIcon, preview: true },
  { file: "tiktok-story.png",      label: "TikTok / Story Image",            size: "220 KB", ratio: "1080×1920",    Icon: ImageIcon, preview: true },
  { file: "instagram-square.png",  label: "Instagram Square",                size: "178 KB", ratio: "1080×1080",    Icon: ImageIcon, preview: true },
  { file: "facebook-landscape.png",label: "Facebook / X Landscape (old)",    size: "102 KB", ratio: "1200×628",     Icon: ImageIcon, preview: true },
];

const CAPTION = `They cancelled. They still got charged. 😤

I built a tool that drafts the exact 4-message refund script that recovers your money. Copy → paste → win.

🎁 Only the first 100 signups get their first case free. After that: $19 per case.

👉 https://powerhouserefunds.com

#subscriptionrefund #cancelledbutcharged #consumertips`;

const START_URL = "https://powerhouserefunds.com";
const FALLBACK_URL = "https://claim-your-refund.emergent.host";

export default function AdDownloads() {
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success(`${label} copied!`);
      } catch { toast.error("Could not copy — long-press and copy manually."); }
    }
  };
  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      <Nav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-900" data-testid="ad-downloads-back-link">← Back to home</Link>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-4">Ad Kit</h1>
        <p className="text-slate-600 mt-2">Tap Download on any file. Save to your phone, then upload to TikTok / Instagram / Facebook.</p>

        <div className="space-y-4 mt-8" data-testid="ad-downloads-grid">
          {ASSETS.map((a) => {
            const url = `/ads/${a.file}`;
            const Icon = a.Icon;
            return (
              <Card key={a.file} className="trust-card" data-testid={`ad-card-${a.file}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-4 flex-wrap">
                    <div className="w-12 h-12 shrink-0 rounded-xl bg-[#EEF4FF] text-[#1E3A8A] grid place-items-center">
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">{a.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{a.ratio} · {a.size}</p>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <a href={url} target="_blank" rel="noreferrer" className="flex-1 sm:flex-none">
                        <Button variant="outline" className="rounded-full w-full" data-testid={`ad-preview-${a.file}`}>Preview</Button>
                      </a>
                      <a href={url} download={a.file} className="flex-1 sm:flex-none">
                        <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] w-full" data-testid={`ad-download-${a.file}`}>
                          <Download className="w-4 h-4 mr-2" /> Download
                        </Button>
                      </a>
                    </div>
                  </div>

                  {a.file.endsWith(".mp4") && (
                    <video controls preload="metadata" className="w-full mt-4 rounded-lg bg-black" style={{maxHeight: 480}}>
                      <source src={url} type="video/mp4" />
                    </video>
                  )}
                  {a.file.endsWith(".gif") && (
                    <img src={url} alt={a.label} className="w-full mt-4 rounded-lg" style={{maxHeight: 480, objectFit: "contain"}} />
                  )}
                  {a.file.endsWith(".png") && (
                    <img src={url} alt={a.label} className="w-full mt-4 rounded-lg" style={{maxHeight: 320, objectFit: "contain"}} />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="trust-card mt-8">
          <CardContent className="p-6">
            <h2 className="font-semibold text-slate-900 mb-1">Your clickable links</h2>
            <p className="text-xs text-slate-500 mb-3">Paste these anywhere — TikTok bio, IG bio, captions, DMs. They become clickable automatically.</p>
            <div className="space-y-2">
              <div className="flex gap-2">
                <a href={START_URL} target="_blank" rel="noreferrer" className="flex-1 min-w-0 truncate px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm text-[#1E3A8A] hover:underline font-mono" data-testid="ad-link-primary">{START_URL}</a>
                <Button onClick={() => copy(START_URL, "Primary link")} variant="outline" className="rounded-full" data-testid="ad-copy-primary-link">
                  <Copy className="w-4 h-4 mr-1.5" /> Copy
                </Button>
              </div>
              <div className="flex gap-2">
                <a href={FALLBACK_URL} target="_blank" rel="noreferrer" className="flex-1 min-w-0 truncate px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-600 hover:underline font-mono" data-testid="ad-link-fallback">{FALLBACK_URL}</a>
                <Button onClick={() => copy(FALLBACK_URL, "Fallback link")} variant="outline" className="rounded-full" data-testid="ad-copy-fallback-link">
                  <Copy className="w-4 h-4 mr-1.5" /> Copy
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">💡 Note: URLs and QR codes <strong>inside</strong> the ad images/video are for scanning only. Always paste one of these links in your caption or bio so viewers can tap it.</p>
          </CardContent>
        </Card>

        <Card className="trust-card mt-4">
          <CardContent className="p-6">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <h2 className="font-semibold text-slate-900">Suggested caption</h2>
              <Button onClick={() => copy(CAPTION, "Caption")} className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="ad-copy-caption">
                <Copy className="w-4 h-4 mr-2" /> Copy caption
              </Button>
            </div>
            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="ad-caption">{CAPTION}</pre>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
